import { useId } from 'react';

import { preferencesSelector, useAppSelector } from '../../../../store';
import { LabelStyleWithRotation } from './EditLabel';
import { resolveLabelEditorStyleState } from './editLabelStyleState';
import { LABEL_VARIANT_OPTIONS, resolveLabelVariant } from '../../shared/labelBadge';

// Slider values this close to the default (fraction of the slider range) stick to it
const STICKY_RANGE = 0.03;

interface EditLabelStyleFieldsProps {
  variant: LabelStyleWithRotation['variant'];
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  color: string;
  backgroundColor: string;
  backgroundOpacity: number;
  borderColor: string;
  padding: number;
  rotation: number;
  skewX: number;
  skewY: number;
  perspectiveX: number;
  perspectiveY: number;
  // Not shown when unset (overlay labels)
  zIndex?: number;
  onAttributeChange: (attribute: keyof LabelStyleWithRotation, value: string | number) => void;
}

type NumericAttribute = 'fontSize' | 'backgroundOpacity' | 'padding' | 'rotation' | 'skewX' | 'skewY'
  | 'perspectiveX' | 'perspectiveY' | 'zIndex';

interface SliderFieldProps {
  label: string;
  attribute: NumericAttribute;
  value: number;
  min: number;
  max: number;
  step?: number;
  // Default value, marked on the slider, which sticks to it
  stickyValue: number;
  onAttributeChange: EditLabelStyleFieldsProps['onAttributeChange'];
}

// Slider with a number input on its right, for values beyond its steps
function SliderField({ label, attribute, value, min, max, step = 1, stickyValue, onAttributeChange }: SliderFieldProps) {
  const ticksId = useId();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => onAttributeChange(attribute, Number(e.target.value));
  const onSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slided = Number(e.target.value);
    const isSticky = Math.abs(slided - stickyValue) <= (max - min) * STICKY_RANGE;
    onAttributeChange(attribute, isSticky ? stickyValue : slided);
  };

  return (
    <div className="theme-select">
      <span className="theme-select__label">{label}</span>
      <div className="label-editor-form__slider-field">
        <input
          aria-label={`${label} slider`}
          className="label-editor-form__range"
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          list={ticksId}
          onChange={onSlide}
        />
        <datalist id={ticksId}>
          <option value={stickyValue} />
        </datalist>
        <input
          aria-label={label}
          className="theme-select__control label-editor-form__slider-input"
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

export function EditLabelStyleFields({
  variant,
  fontSize,
  fontWeight,
  color,
  backgroundColor,
  backgroundOpacity,
  borderColor,
  padding,
  rotation,
  skewX,
  skewY,
  perspectiveX,
  perspectiveY,
  zIndex,
  onAttributeChange,
}: EditLabelStyleFieldsProps) {
  const preferences = useAppSelector(preferencesSelector);
  // Map defaults (for this preset), where sliders stick
  const defaults = resolveLabelEditorStyleState({ ...preferences.defaultLabelStyle, variant });
  const defaultRotation = preferences.defaultLabelRotation ?? 0;
  const isNeon = resolveLabelVariant(variant) === 'neon';
  // Neon borders follow the background colour: the border colour is the label one
  const borderColorLabel = isNeon ? 'Label colour' : 'Border colour';
  const visibleBorderColor = borderColor === 'transparent'
    ? (isNeon ? backgroundColor : '#333333')
    : borderColor;

  return (
    <>
      <SliderField
        label="Font size"
        attribute="fontSize"
        stickyValue={defaults.fontSize}
        value={fontSize}
        min={8}
        max={72}
        onAttributeChange={onAttributeChange}
      />
      <label className="theme-toggle">
        <span className="theme-toggle__label">Bold</span>
        <input
          type="checkbox"
          checked={fontWeight === 'bold'}
          onChange={(e) => onAttributeChange('fontWeight', e.target.checked ? 'bold' : 'normal')}
        />
      </label>
      <SliderField
        label="Padding"
        attribute="padding"
        stickyValue={defaults.padding}
        value={padding}
        min={0}
        max={40}
        onAttributeChange={onAttributeChange}
      />
      <SliderField
        label="Rotation (°)"
        attribute="rotation"
        stickyValue={defaultRotation}
        value={rotation}
        min={-180}
        max={180}
        onAttributeChange={onAttributeChange}
      />
      <SliderField
        label="Horizontal skew (°)"
        attribute="skewX"
        stickyValue={0}
        value={skewX}
        min={-60}
        max={60}
        onAttributeChange={onAttributeChange}
      />
      <SliderField
        label="Vertical skew (°)"
        attribute="skewY"
        stickyValue={0}
        value={skewY}
        min={-60}
        max={60}
        onAttributeChange={onAttributeChange}
      />
      <SliderField
        label="Horizontal perspective (°)"
        attribute="perspectiveX"
        stickyValue={0}
        value={perspectiveX}
        min={-70}
        max={70}
        onAttributeChange={onAttributeChange}
      />
      <SliderField
        label="Vertical perspective (°)"
        attribute="perspectiveY"
        stickyValue={0}
        value={perspectiveY}
        min={-70}
        max={70}
        onAttributeChange={onAttributeChange}
      />
      <label className="theme-select">
        <span className="theme-select__label">Style preset</span>
        <select
          aria-label="Style preset"
          className="theme-select__control"
          value={resolveLabelVariant(variant)}
          onChange={(e) => onAttributeChange('variant', e.target.value)}
        >
          {LABEL_VARIANT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label className="theme-color">
        <div className="theme-color__text">
          <span className="theme-color__label">Text colour</span>
        </div>
        <div className="theme-color__control">
          <input
            aria-label="Text colour"
            type="color"
            value={color}
            onChange={(e) => onAttributeChange('color', e.target.value)}
          />
          <span className="theme-color__value">{color}</span>
        </div>
      </label>
      <label className="theme-color">
        <div className="theme-color__text">
          <span className="theme-color__label">{borderColorLabel}</span>
        </div>
        <div className="theme-color__control">
          <input
            aria-label={borderColorLabel}
            type="color"
            value={visibleBorderColor}
            onChange={(e) => onAttributeChange('borderColor', e.target.value)}
          />
          <span className="theme-color__value">{visibleBorderColor}</span>
        </div>
      </label>
      <label className="theme-color">
        <div className="theme-color__text">
          <span className="theme-color__label">Background colour</span>
        </div>
        <div className="theme-color__control">
          <input
            aria-label="Background colour"
            type="color"
            value={backgroundColor}
            onChange={(e) => onAttributeChange('backgroundColor', e.target.value)}
          />
          <span className="theme-color__value">{backgroundColor}</span>
        </div>
      </label>
      <SliderField
        label="Background opacity"
        attribute="backgroundOpacity"
        stickyValue={defaults.backgroundOpacity}
        value={backgroundOpacity}
        min={0}
        max={1}
        step={0.01}
        onAttributeChange={onAttributeChange}
      />
      {zIndex !== undefined && (
        <SliderField
          label="Z-index"
          attribute="zIndex"
          stickyValue={defaults.zIndex}
          value={zIndex}
          min={0}
          max={9999}
          onAttributeChange={onAttributeChange}
        />
      )}
    </>
  );
}

export default EditLabelStyleFields;
